import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, ColorPickerBox, Column, Row, Spinner, Text} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {InfoButton} from "web-vcore";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld} from "../../../../../../Server/DB/mapObjects/$mapObject.js";
import {PathFill} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {SpinnerForPercent} from "../../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {TexturePicker} from "../../../../../../Utils/ReactComponents/TexturePicker.js";
import {Vec2} from "../../../../../../Utils/ThreeJS/VectorStructs.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";

export const PathEditorTab_Fill = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {map, obj, path, index, Change} = props;
	const objPos = ResolveObjPosInWorld(obj);
	const allMapObjects = GetMapObjects(map.id);
	const pathIdDuplicate = !!path.path_id && (allMapObjects ?? []).some(obj2=>{
		return (obj2.paths ?? []).some(path2=>{
			if (obj2.id === obj.id && obj2.paths!.indexOf(path2) === index) return false; // skip self
			return path2.path_id === path.path_id;
		});
	});

	return <>
		<Row>
			<Text>Fill:</Text>
			<CheckBox ml={5} text="Enabled" value={path.fill != null && !path.fill.renderDisabled} onChange={val=>{
				Change(a=>{
					if (val) {
						// First enable: create the struct if it doesn't exist yet (preserving the
						// "if they're trying to enable and the struct doesn't exist yet, THEN add" rule).
						// Subsequent toggles clear renderDisabled (set to undefined), preserving the config.
						a.fill ??= new PathFill({});
						a.fill.renderDisabled = undefined;
					} else {
						// Disable in-place (keep the struct + its config; the renderer skips
						// fill-meshes with renderDisabled set).
						if (a.fill) a.fill.renderDisabled = true;
					}
				});
			}}/>
			<Button ml={5} mdIcon="delete" title="Delete fill data (asks for confirmation)" onClick={()=>{
				ShowMessageBox({
					title: "Delete fill?", cancelButton: true,
					message: `
						Permanently delete this path's fill-mesh data?
						This removes the whole configuration (color, opacity, texture, z-offset).
					`.AsMultiline(0),
					onOK: ()=>{
						Change(a=>{
							a.fill = undefined;
						});
					},
				});
			}}/>
		</Row>
		{path.fill && <>
			<Row mt={5}>
				<Text>Color:</Text>
				<ColorPickerBox ml={5} color={(path.fill.color ?? [255,255,255]) as [number,number,number]} onChange={val=>Change(a=>a.fill!.color = val)}/>
				<InfoButton ml={5} text={`
					If set, and there's no texture, this color will be used as the fill's color.
					If set, and the fill also has a texture, this color will be used to colorize it. (with the alpha controlling the colorization intensity)
					Note: When fills are assigned a color, they end up showing a bit darker in-map than that color would normally appear, eg. on html elements.
				`.AsMultiline(0)}/>
				<Text ml={5}>Opacity:</Text>
				<SpinnerForPercent ml={5} style={{width: 50}} value={path.fill.opacity} onChange={val=>Change(a=>a.fill!.opacity = val)} title="Opacity of the fill. (for colorization intensity, instead set color's alpha)"/>
			</Row>
			<Row mt={5}>
				<Text>Texture:</Text>
				<TexturePicker ml={5} value={path.fill.texture_id} onChange={val=>Change(a=>a.fill!.texture_id = val)}/>
				<Column ml={5} style={{flex: 1}}>
					<Row style={{flex: 1}}>
						<Text>Size, X (m):</Text>
						<Spinner ml={5} min={0} step={0.1} style={{width: 50}} value={path.fill.texture_size?.x} onChange={val=>Change(a=>a.fill!.texture_size = new Vec2(val, a.fill!.texture_size?.y ?? 0))}/>
						<Text ml={5}>Y (m):</Text>
						<Spinner ml={5} min={0} step={0.1} style={{width: 50}} value={path.fill.texture_size?.y} onChange={val=>Change(a=>a.fill!.texture_size = new Vec2(a.fill!.texture_size?.x ?? 0, val))}/>
					</Row>
					<Row style={{flex: 1}}>
						<Text>Rotation:</Text>
						<Spinner ml={5} step="any" style={{width: 50}} value={path.fill.texture_rotation} onChange={val=>Change(a=>a.fill!.texture_rotation = val)}/>
						<Button ml={5} p="2px 5px" text="-90" onClick={()=>Change(a=>{
							const newRot = ((a.fill!.texture_rotation ?? 0) - 90) % 360;
							a.fill!.texture_rotation = newRot < 0 ? newRot + 360 : newRot;
						})}/>
						<Button ml={5} p="2px 5px" text="+90" onClick={()=>Change(a=>{
							const newRot = ((a.fill!.texture_rotation ?? 0) + 90) % 360;
							a.fill!.texture_rotation = newRot < 0 ? newRot + 360 : newRot;
						})}/>
						<Button ml={5} p="2px 5px" text="Fix" onClick={()=>Change(a=>a.fill!.texture_rotation = 0)}/>
					</Row>
				</Column>
			</Row>
			<Row mt={5}>
				<Text>Z offset:</Text>
				<Spinner ml={5} min={Number.MIN_SAFE_INTEGER} step="any" value={path.fill.zOffset} onChange={val=>Change(a=>a.fill!.zOffset = val)}/>
				<Text ml={5}>(world: {objPos.z + (path.fill.zOffset ?? 0)})</Text>
			</Row>
		</>}
	</>;
});